import React, { useEffect, useState } from 'react';

import { config } from '../../../../config/index';
import { newFetch, PUT, GET } from '../../../../helpers/fetch';
import placeholderImage from '../../../../assets/images/placeholder.jpg';
import AlertService from '../../../../components/SweetAlert';
import Searchbar from '../../../../components/Searchbar';
import ToastUtil from '../../../../components/ToastNotification/ToastUtil';

const { API_URL, SERVER_URL } = config;

const AssignAssociateSidebar = ({
  activeSite,
  setSpreadsheetViewAssignShift,
  spreadsheetViewAssignShift,
  startOfMonth,
  lastDayOfMonth,
  shifts,
  fetchAssociatesByMonth,
  selectedSiteID,
  sites = [],
}) => {
  const token = localStorage.getItem('token');
  const [associates, setAssociates] = useState([]);
  const [filteredAssociates, setFilteredAssociates] = useState([]);
  const [selectedAssociates, setSelectedAssociates] = useState([]);
  const [dateSelected, setdateSelected] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const dateSelectedHandler = (e) => setdateSelected(e.target.value);
  const allSitesSelected = Number(selectedSiteID) === 0;
  const selectedShiftDetails = shifts.find(
    (shift) => Number(shift.value) === Number(selectedShift)
  );
  const targetSiteId = allSitesSelected
    ? selectedShiftDetails?.site_id
    : activeSite?.id;
  const associateBelongsToTargetSite = (associate) =>
    !targetSiteId ||
    associate.sites?.some(
      (site) => Number(site.id) === Number(targetSiteId)
    );
  const visibleAssociates =
    selectedShift && targetSiteId
      ? filteredAssociates.filter(associateBelongsToTargetSite)
      : filteredAssociates;

  const fetchScheduleId = async () => {
    try {
      let api_path = `/schedule/date/${dateSelected}/shift/${selectedShift}`;
      let responseData = await newFetch(GET, api_path);

      if (responseData.success) {
        if (responseData?.data?.id) {
          setSelectedScheduleId(responseData.data.id);
        } else {
          setSelectedScheduleId(null);
          ToastUtil.error('No Schedule for this day.');
        }
        ToastUtil.success(responseData.message);
      } else {
        ToastUtil.error('No Schedule for this day.');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const searchHandler = (e) => {
    let filtered = [...associates].filter((associate) => {
      let name = `${associate.first_name} ${associate.last_name}`;
      return name.toUpperCase().indexOf(e.target.value.toUpperCase()) > -1;
    });

    setFilteredAssociates(filtered);
  };

  const selectRemoveAssociateHandler = (associate) => {
    if (!associateBelongsToTargetSite(associate)) {
      ToastUtil.error('Associate is not assigned to the selected shift site.');
      return;
    }

    const associate_id = associate.id;
    if (selectedAssociates.some((id) => id === associate_id)) {
      setSelectedAssociates(
        [...selectedAssociates].filter((id) => id !== associate_id)
      );
    } else {
      setSelectedAssociates([...selectedAssociates, associate_id]);
    }
  };

  const selectAllHandler = (e) => {
    e.preventDefault();
    setSelectedAssociates(
      visibleAssociates.map((associate) => associate.id)
    );
  };

  const removeAllHandler = (e) => {
    e.preventDefault();
    setSelectedAssociates([]);
  };

  const handleUpdateShiftStatus = async () => {
    if (isSaving) return false;

    if (!selectedShift) {
      ToastUtil.error('Please select shift.');
      return false;
    }

    if (selectedAssociates.length === 0) {
      ToastUtil.error('Please select at least one associate.');
      return false;
    }

    const invalidAssociates = selectedAssociates.filter((associateId) => {
      const associate = associates.find(
        (item) => Number(item.id) === Number(associateId)
      );

      return !associate || !associateBelongsToTargetSite(associate);
    });

    if (invalidAssociates.length > 0) {
      ToastUtil.error(
        'Associates can only be assigned to shifts from their assigned sites.'
      );
      return false;
    }

    if (!dateSelected) {
      ToastUtil.error('Please select date.');
      return false;
    }

    if (!selectedScheduleId) {
      ToastUtil.error('No Schedule for this day.');
      return false;
    }

    const isConfirmed = await AlertService.confirm(
      '',
      'Are you sure you want to assign the Associates to this shift?'
    );
    if (isConfirmed) {
      let url = `/associate/shifts/${selectedScheduleId}/assign`;

      try {
        setIsSaving(true);
        let data = { associate_ids: selectedAssociates };
        let responseData = await newFetch(PUT, url, data);

        if (responseData?.success) {
          ToastUtil.success(responseData.message);
          setSpreadsheetViewAssignShift(false);
          fetchAssociatesByMonth(1);

          // Clear Shift Date and Shift Timeslot data after successful save
          setdateSelected(null);
          setSelectedShift(null);
          setSelectedScheduleId(null);
          setSelectedAssociates([]);
        } else {
          const failedAssociate = responseData?.data?.fail?.find(
            (failAssociate) =>
              selectedAssociates.includes(failAssociate.associate_id)
          );
          ToastUtil.error(
            failedAssociate?.message ||
              responseData?.message ||
              'Failed to assign associates.'
          );
        }
      } catch (error) {
        console.log(error);
        ToastUtil.error('Failed to assign associates.');
      } finally {
        setIsSaving(false);
      }
    }
  };

  useEffect(() => {
    if (dateSelected && selectedShift) {
      fetchScheduleId();
    }
  }, [dateSelected, selectedShift]);

  useEffect(() => {
    const fetchAssociates = async () => {
      const siteIds = allSitesSelected
        ? sites.map((site) => site.id).filter((siteId) => Number(siteId) > 0)
        : [targetSiteId];
      const responses = await Promise.all(
        siteIds.map((siteId) =>
          newFetch(GET, `/site/${siteId}/associates`).catch(() => null)
        )
      );
      const siteAssociates = responses.reduce((list, response) => {
        if (response?.success && Array.isArray(response.data)) {
          return [...list, ...response.data];
        }

        return list;
      }, []);
      const uniqueAssociates = Array.from(
        new Map(
          siteAssociates.map((associate) => [associate.id, associate])
        ).values()
      );

      setAssociates(uniqueAssociates);
      setFilteredAssociates(JSON.parse(JSON.stringify(uniqueAssociates)));
    };

    if (
      spreadsheetViewAssignShift &&
      (allSitesSelected ? sites.length > 0 : targetSiteId)
    ) {
      fetchAssociates();
    } else if (spreadsheetViewAssignShift) {
      setAssociates([]);
      setFilteredAssociates([]);
      setSelectedAssociates([]);
    }
  }, [spreadsheetViewAssignShift, targetSiteId, allSitesSelected, sites]);

  return (
    <React.Fragment>
      {spreadsheetViewAssignShift && (
        <div
          className="right-sidebar-menu pb-4"
          style={{ overflowY: 'auto', width: 505, zIndex: '200' }}
        >
          <div
            className="x-button-container"
            style={{ marginTop: '1.5rem', marginRight: '.5rem' }}
          >
            <button
              className="x-button"
              onClick={() =>
                setSpreadsheetViewAssignShift(!spreadsheetViewAssignShift)
              }
            >
              <i
                className="fas fa-remove text-muted"
                style={{ cursor: 'pointer', fontSize: 18 }}
              ></i>
            </button>
          </div>
          <div className="px-5 border-bottom">
            <div className="pb-2 py-4">
              <h6 style={{ fontSize: '1rem' }}>
                Assign Shift |{' '}
                {new Date(startOfMonth).toLocaleString('en-US', {
                  month: 'long',
                })}{' '}
                {new Date(startOfMonth).getFullYear()}
              </h6>
            </div>
          </div>
          <div className="mt-4" style={{ fontSize: 14 }}>
            <div className="row">
              <div className="col-4 align-self-center text-end">
                <span>Shift Date </span>
              </div>
              <div className="col-8">
                <input
                  name="dateSelected"
                  type="date"
                  className="form-control adjust-fs text-muted"
                  style={{ width: 305 }}
                  min={startOfMonth}
                  max={lastDayOfMonth}
                  value={dateSelected || ''}
                  onChange={(e) => dateSelectedHandler(e)}
                />
              </div>
            </div>
            <div className="row mt-4">
              <div className="col-4 align-self-center text-end">
                <span>Shift Timeslot </span>
              </div>
              <div className="col-8">
                <select
                  name="shift"
                  id="shift"
                  className="form-select mt-2 adjust-fs text-muted"
                  style={{ width: 305 }}
                  value={selectedShift || ''}
                  onChange={({ target: { value } }) => {
                    setSelectedShift(value);
                    setSelectedScheduleId(null);
                    setSelectedAssociates([]);
                  }}
                >
                  <option value={''}>Select Shift</option>
                  {shifts.length > 0 &&
                    shifts.map((shift, index) => (
                      <option key={index} value={shift.value}>
                        {shift.label}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div
              className="table-responsive border mt-5 mx-5"
              style={{ width: '420px' }}
            >
              <div className="d-flex flex-column px-4 py-4">
                <div className="d-flex justify-content-between">
                  <div className="mb-3">Selected Associates </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between">
                      {selectedAssociates.length === 0 ? (
                        <a
                          href="javascipt:void(0)"
                          onClick={(e) => selectAllHandler(e)}
                        >
                          Select All
                        </a>
                      ) : selectedAssociates.length ===
                        visibleAssociates.length ? (
                        <a
                          href="javascipt:void(0)"
                          onClick={(e) => removeAllHandler(e)}
                        >
                          Remove All
                        </a>
                      ) : (
                        <a
                          href="javascipt:void(0)"
                          onClick={(e) => removeAllHandler(e)}
                        >
                          Remove ({selectedAssociates.length})
                        </a>
                      )}
                    </div>{' '}
                  </div>
                </div>

                <Searchbar
                  search={(e) => searchHandler(e)}
                  placeholder="Search associates"
                  width="100%"
                />
                <div className="overflow-auto pe-2" style={{ height: 410 }}>
                  {visibleAssociates.map((associate) => (
                    <div
                      key={associate.id}
                      className="d-flex align-items-center my-1 py-1 rounded"
                      onClick={() => selectRemoveAssociateHandler(associate)}
                      style={{
                        ...(selectedAssociates.some(
                          (id) => id === associate.id
                        )
                          ? { backgroundColor: '#f1f1f1' }
                          : {}),
                      }}
                    >
                      <div className="mx-2">
                        <img
                          className="rounded-circle border me-3"
                          src={
                            associate.image
                              ? `${SERVER_URL}/${associate.image}`
                              : placeholderImage
                          }
                          alt="username-img"
                          style={{ height: '35px', width: '35px' }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = placeholderImage;
                          }}
                        />
                      </div>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <div>
                          {`${associate.first_name} ${associate.last_name}`
                            .length > 18
                            ? `${associate.first_name} ${associate.last_name}`.substring(
                                0,
                                18
                              ) + '...'
                            : `${associate.first_name} ${associate.last_name}`}
                        </div>
                        <div className="me-2">
                          {selectedAssociates.some(
                            (id) => id === associate.id
                          ) && <i className="fas fa-check text-muted" />}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="row text-end px-4">
              <div className="col-12 py-4">
                <button
                  className="btn btn-primary btn-sm"
                  style={{ width: 120 }}
                  disabled={isSaving}
                  onClick={() => {
                    handleUpdateShiftStatus();
                  }}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {spreadsheetViewAssignShift && (
        <div
          className="modal-backdrop fade in"
          style={{ opacity: '0.3', zIndex: '100' }}
          onClick={() => {
            setSpreadsheetViewAssignShift(false);
          }}
        ></div>
      )}
    </React.Fragment>
  );
};

export default AssignAssociateSidebar;
